import React, { useContext, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import FormField from '../components/FormField';
import CollectiblePreviewSurface from '../components/CollectiblePreviewSurface';
import TopBar from '../components/TopBar';
import { UserContext } from '../App';
import { db, functions, storage } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './ProductScheduleEditor.module.css';
import { normalizeCurrency, SUPPORTED_CURRENCIES } from '../utils/currency';
import { getCollectibleDisplayImagePath, isCollectibleMediaReady, isVideoCollectibleMedia, useCollectibleMediaUrls } from '../utils/collectibleMedia';
import { createVersionToken, getArtistSignaturePath, useStorageUrl } from '../utils/storageMedia';
import { getRoleAccessState, ROLE_ARTIST } from '../utils/roles';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import { buildTimezoneOptions, getBrowserTimezone, normalizeTimezone, parseScheduledDateTimeInTimezone, resolveLegacyDraftTimezone, timezoneLabel } from '../utils/timezones';

const AUDIO_ACCEPT = '.mp3,.wav,.m4a,.aac,audio/mpeg,audio/wav,audio/mp4,audio/aac';
const AUDIO_MAX_DURATION_SECONDS = 21;
const AUDIO_FILE_TYPES_LABEL = 'MP3, WAV, M4A, or AAC';

function toDateTimeLocalValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function createEmptyDraft(appUser) {
  const initialTimezone = normalizeTimezone(appUser?.settings?.timezone || getBrowserTimezone(), 'UTC');
  return {
    title: '',
    description: '',
    priceAmount: '',
    currency: normalizeCurrency(appUser?.settings?.currency || 'USD'),
    maxSupply: '1',
    saleStartAt: toDateTimeLocalValue(new Date(Date.now() + (60 * 60 * 1000))),
    timezone: initialTimezone,
    durationHours: 1,
    media: [],
    soundtrack: null,
  };
}

function createEmptyMediaEditor() {
  return {
    file: null,
    previewUrl: '',
    crop: { x: 0, y: 0 },
    zoom: 1,
    croppedAreaPixels: null,
    sourceWidth: 0,
    sourceHeight: 0,
    durationSeconds: 0,
    localStatus: '',
  };
}

function createEmptyAudioEditor() {
  return {
    file: null,
    durationSeconds: 0,
    localStatus: '',
  };
}

function getVideoExtension(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.m4v')) return 'm4v';
  if (name.endsWith('.mov')) return 'mov';
  return 'mp4';
}

function getAudioExtension(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.wav')) return 'wav';
  if (name.endsWith('.m4a')) return 'm4a';
  if (name.endsWith('.aac')) return 'aac';
  return 'mp3';
}

function isAcceptedVideoFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type.startsWith('video/') || ['.mp4', '.mov', '.m4v'].some((ext) => name.endsWith(ext));
}

function isAcceptedAudioFile(file) {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type.startsWith('audio/') || ['.mp3', '.wav', '.m4a', '.aac'].some((ext) => name.endsWith(ext));
}

function addHoursToLocalDateTimeValue(value, hours) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    0,
    0
  ));
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCHours(date.getUTCHours() + Number(hours || 0));
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const nextHours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${nextHours}:${minutes}`;
}

function formatDraftDateTime(value, timezone) {
  const date = parseScheduledDateTimeInTimezone(value, timezone);
  if (!date || Number.isNaN(date.getTime())) return 'TBD';
  const formatter = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: normalizeTimezone(timezone, 'UTC'),
  });
  return `${formatter.format(date)} ${timezoneLabel(timezone, date)}`;
}

function formatSeconds(value) {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return '0.0s';
  return `${seconds.toFixed(1)}s`;
}

async function loadVideoMetadata(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => resolve({
        width: Math.round(Number(video.videoWidth || 0)),
        height: Math.round(Number(video.videoHeight || 0)),
        durationSeconds: Number(video.duration || 0),
      });
      video.onerror = () => reject(new Error('Invalid video file.'));
      video.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function loadAudioMetadata(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => resolve({
        durationSeconds: Number(audio.duration || 0),
      });
      audio.onerror = () => reject(new Error('Invalid audio file.'));
      audio.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function DropWizard() {
  const appUser = useContext(UserContext);
  const artistAccess = getRoleAccessState(appUser, ROLE_ARTIST);
  const connectStatus = deriveActiveConnectStatus(appUser);
  const navigate = useNavigate();
  const location = useLocation();
  const requestedDropId = useMemo(() => new URLSearchParams(location.search).get('draft') || '', [location.search]);
  const [artistId, setArtistId] = useState('');
  const [artist, setArtist] = useState(null);
  const [dropId, setDropId] = useState('');
  const [draft, setDraft] = useState(() => createEmptyDraft(appUser));
  const [mediaEditor, setMediaEditor] = useState(() => createEmptyMediaEditor());
  const [audioEditor, setAudioEditor] = useState(() => createEmptyAudioEditor());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [processingMedia, setProcessingMedia] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(createEmptyDraft(appUser));
  }, [appUser]);

  useEffect(() => () => {
    if (mediaEditor.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(mediaEditor.previewUrl);
  }, [mediaEditor.previewUrl]);

  useEffect(() => {
    if (!appUser?.id) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const artistSnap = await getDocs(query(collection(db, 'artists'), where('artistOwnerUid', '==', appUser.id), limit(1)));
        const ownedArtistDoc = artistSnap.docs[0] || null;
        const ownedArtistId = ownedArtistDoc?.id || '';
        if (!active) return;
        setArtistId(ownedArtistId);
        setArtist(ownedArtistDoc ? { id: ownedArtistDoc.id, ...(ownedArtistDoc.data() || {}) } : null);
        if (!requestedDropId) {
          setDropId('');
          setDraft(createEmptyDraft(appUser));
          setMediaEditor(createEmptyMediaEditor());
          setAudioEditor(createEmptyAudioEditor());
          setError('');
          return;
        }
        const snap = await getDoc(doc(db, 'drops', String(requestedDropId)));
        if (!active) return;
        if (!snap.exists()) throw new Error('Drop draft not found.');
        const data = snap.data() || {};
        if (String(data.artistOwnerUid || '') !== String(appUser.id || '')) throw new Error('Only the artist owner can edit this drop.');
        const savedDraft = data.draft || {};
        const { utcOffset, ...savedDraftWithoutLegacyOffset } = savedDraft;
        void utcOffset;
        const nextDraft = {
          ...createEmptyDraft(appUser),
          ...savedDraftWithoutLegacyOffset,
          currency: normalizeCurrency(data?.draft?.currency || appUser?.settings?.currency || 'USD'),
          timezone: normalizeTimezone(
            data?.draft?.timezone
              || data?.timezone
              || resolveLegacyDraftTimezone({
                userTimezone: appUser?.settings?.timezone,
                browserTimezone: getBrowserTimezone(),
              }),
            'UTC'
          ),
          maxSupply: String(data?.draft?.maxSupply || '1'),
          durationHours: Number(data?.draft?.durationHours || 1),
          media: Array.isArray(data?.draft?.media) ? data.draft.media.slice(0, 1) : [],
          soundtrack: data?.draft?.soundtrack || null,
        };
        setDropId(snap.id);
        setDraft(nextDraft);
        setMediaEditor({
          ...createEmptyMediaEditor(),
          zoom: Number(nextDraft?.media?.[0]?.composition?.zoom || 1),
          croppedAreaPixels: nextDraft?.media?.[0]?.composition?.crop || null,
          sourceWidth: Number(nextDraft?.media?.[0]?.source?.width || 0),
          sourceHeight: Number(nextDraft?.media?.[0]?.source?.height || 0),
          durationSeconds: Number(nextDraft?.media?.[0]?.source?.durationSeconds || 0),
          localStatus: String(nextDraft?.media?.[0]?.processing?.status || ''),
        });
        setAudioEditor({
          ...createEmptyAudioEditor(),
          durationSeconds: Number(nextDraft?.soundtrack?.source?.durationSeconds || nextDraft?.soundtrack?.durationSeconds || 0),
          localStatus: String(nextDraft?.soundtrack?.processing?.status || ''),
        });
        setError('');
      } catch (err) {
        if (active) setError(err?.message || 'Unable to load drop draft.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser, requestedDropId]);

  const persistDraft = async (nextDraft = draft, nextDropId = dropId) => {
    if (!artistId) throw new Error('Create your artist profile before building a drop.');
    const callable = httpsCallable(functions, nextDropId ? 'updateDropDraft' : 'createDropDraft');
    const response = await callable({
      artistId,
      dropId: nextDropId,
      draft: nextDraft,
    });
    const savedDropId = String(response?.data?.dropId || '');
    setDropId(savedDropId);
    return savedDropId;
  };

  const ensureDropId = async () => {
    if (dropId) return dropId;
    return persistDraft(draft, '');
  };

  const updateField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const savedDropId = await persistDraft();
      navigate(`/creator/drops/new?draft=${savedDropId}`, { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to save drop draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setError('');
      const metadata = await loadVideoMetadata(file);
      if (!isAcceptedVideoFile(file)) throw new Error('Only MP4, MOV, and M4V video files are supported.');
      if (metadata.durationSeconds <= 0 || metadata.durationSeconds > 10.5) throw new Error('Drop videos must be about 10 seconds or shorter.');
      setMediaEditor((current) => {
        if (current.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(current.previewUrl);
        return {
          file,
          previewUrl: URL.createObjectURL(file),
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
          sourceWidth: metadata.width,
          sourceHeight: metadata.height,
          durationSeconds: metadata.durationSeconds,
          localStatus: 'selected',
        };
      });
    } catch (err) {
      setError(err?.message || 'Unable to load video.');
    }
  };

  const handleAudioChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setError('');
      const metadata = await loadAudioMetadata(file);
      if (!isAcceptedAudioFile(file)) throw new Error(`Only ${AUDIO_FILE_TYPES_LABEL} audio files are supported.`);
      if (metadata.durationSeconds <= 0 || metadata.durationSeconds > AUDIO_MAX_DURATION_SECONDS) {
        throw new Error(`Drop music must be ${AUDIO_MAX_DURATION_SECONDS} seconds max.`);
      }
      setAudioEditor({
        file,
        durationSeconds: metadata.durationSeconds,
        localStatus: 'selected',
      });
    } catch (err) {
      setError(err?.message || 'Unable to load audio.');
    }
  };

  const handleProcessMedia = async () => {
    const existingMedia = draft.media?.[0] || null;
    const existingSoundtrack = draft.soundtrack || existingMedia?.soundtrack || null;
    if (!mediaEditor.file && !existingMedia?.source?.storagePath) {
      setError('Upload a video before processing.');
      return;
    }
    if (!mediaEditor.previewUrl && !mediaEditor.croppedAreaPixels && !existingMedia?.composition?.crop) {
      setError('Choose a video again so you can set the circular crop.');
      return;
    }
    if (!audioEditor.file && !existingSoundtrack?.source?.storagePath) {
      setError('Upload music before processing.');
      return;
    }
    try {
      setProcessingMedia(true);
      setError('');
      const nextDropId = await ensureDropId();
      const sourcePath = mediaEditor.file
        ? `drops/${nextDropId}/media/source-v${createVersionToken()}.${getVideoExtension(mediaEditor.file)}`
        : String(existingMedia?.source?.storagePath || '').trim();
      const soundtrackSourcePath = audioEditor.file
        ? `drops/${nextDropId}/media/soundtrack-source-v${createVersionToken()}.${getAudioExtension(audioEditor.file)}`
        : String(existingSoundtrack?.source?.storagePath || '').trim();
      if (mediaEditor.file) {
        await uploadBytes(ref(storage, sourcePath), mediaEditor.file, { contentType: mediaEditor.file.type || 'video/mp4' });
      }
      if (audioEditor.file) {
        await uploadBytes(ref(storage, soundtrackSourcePath), audioEditor.file, { contentType: audioEditor.file.type || 'audio/mpeg' });
      }
      setMediaEditor((prev) => ({ ...prev, localStatus: 'processing' }));
      setAudioEditor((prev) => ({ ...prev, localStatus: 'processing' }));
      const composition = {
        crop: mediaEditor.croppedAreaPixels || existingMedia?.composition?.crop || null,
        zoom: Number(mediaEditor.zoom || existingMedia?.composition?.zoom || 1),
        sourceWidth: Number(mediaEditor.sourceWidth || existingMedia?.source?.width || 0),
        sourceHeight: Number(mediaEditor.sourceHeight || existingMedia?.source?.height || 0),
        posterTimeSeconds: Number(existingMedia?.composition?.posterTimeSeconds || 1),
      };
      const callable = httpsCallable(functions, 'processDropDraftMedia');
      const response = await callable({
        artistId,
        dropId: nextDropId,
        sourcePath,
        sourceMimeType: mediaEditor.file?.type || existingMedia?.source?.mimeType || 'video/mp4',
        sourceDurationSeconds: Number(mediaEditor.durationSeconds || existingMedia?.source?.durationSeconds || 0),
        sourceWidth: composition.sourceWidth,
        sourceHeight: composition.sourceHeight,
        soundtrackSourcePath,
        soundtrackSourceMimeType: audioEditor.file?.type || existingSoundtrack?.source?.mimeType || 'audio/mpeg',
        soundtrackDurationSeconds: Number(audioEditor.durationSeconds || existingSoundtrack?.source?.durationSeconds || existingSoundtrack?.durationSeconds || 0),
        composition,
      });
      const nextMedia = response?.data?.media || null;
      if (!nextMedia) throw new Error('Processed video is missing.');
      const nextDraft = {
        ...draft,
        media: [nextMedia],
        soundtrack: nextMedia.soundtrack || null,
      };
      setDraft(nextDraft);
      await persistDraft(nextDraft, nextDropId);
      setMediaEditor((prev) => ({
        ...prev,
        file: null,
        croppedAreaPixels: composition.crop,
        localStatus: 'ready',
      }));
      setAudioEditor({
        ...createEmptyAudioEditor(),
        durationSeconds: Number(nextMedia?.soundtrack?.durationSeconds || 0),
        localStatus: 'ready',
      });
    } catch (err) {
      setError(err?.message || 'Unable to process media.');
      setMediaEditor((prev) => ({ ...prev, localStatus: 'failed' }));
      setAudioEditor((prev) => ({ ...prev, localStatus: 'failed' }));
    } finally {
      setProcessingMedia(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      setError('');
      const nextDropId = await persistDraft();
      const callable = httpsCallable(functions, 'publishDrop');
      await callable({ dropId: nextDropId });
      navigate(`/creator/drops/${nextDropId}`);
    } catch (err) {
      setError(err?.message || 'Unable to publish drop.');
    } finally {
      setPublishing(false);
    }
  };

  const renderedPreviewPath = getCollectibleDisplayImagePath({ media: draft.media || [] });
  const { mediaUrl: renderedPreviewUrl, playbackUrl: renderedPlaybackUrl, posterUrl: renderedPosterUrl } = useCollectibleMediaUrls({ media: draft.media || [] });
  const artistSignatureUrl = useStorageUrl(getArtistSignaturePath(artist || {}));
  const mediaReady = isCollectibleMediaReady({ media: draft.media || [], soundtrack: draft.soundtrack });
  const hasPlayableCollectibleVideo = isVideoCollectibleMedia({ media: draft.media || [] }) && Boolean(renderedPlaybackUrl);
  const derivedEnd = useMemo(() => addHoursToLocalDateTimeValue(draft.saleStartAt, draft.durationHours), [draft.durationHours, draft.saleStartAt]);
  const timezoneOptions = useMemo(() => buildTimezoneOptions({
    selectedTimezone: draft.timezone,
    fallbackTimezone: normalizeTimezone(appUser?.settings?.timezone || getBrowserTimezone(), 'UTC'),
  }), [appUser?.settings?.timezone, draft.timezone]);
  const hasVideoSource = Boolean(mediaEditor.previewUrl || draft.media?.[0]?.source?.storagePath);
  const hasAudioSource = Boolean(audioEditor.file || draft.soundtrack?.source?.storagePath);
  const phase = processingMedia
    ? 'processing'
    : mediaReady && !mediaEditor.file && !audioEditor.file
      ? 'preview_ready'
      : hasVideoSource && hasAudioSource
        ? 'media_ready_to_process'
        : hasVideoSource
          ? 'video_selected'
          : 'idle';
  const showFields = phase === 'preview_ready';
  const allowProcessing = phase === 'media_ready_to_process' && Boolean(mediaEditor.croppedAreaPixels || draft.media?.[0]?.composition?.crop);
  const mediaStatus = phase === 'preview_ready' ? 'ready' : (mediaEditor.localStatus || draft.media?.[0]?.processing?.status || 'selected');

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/creator/products')} />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Drop Builder</span>
          <h1>{dropId ? 'Edit Drop Draft' : 'Create Drop'}</h1>
          <p>Upload the video first, add the soundtrack, process the collectible, then finish the release details.</p>
        </header>

        {!connectStatus.canPayout ? <p className={styles.notice}>You can publish and sell this drop now. Your creator share will stay held until Stripe payout setup is complete.</p> : null}
        {loading ? <p className={styles.notice}>Loading drop builder…</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}

        {!loading ? (
          <form className={styles.form} onSubmit={(event) => event.preventDefault()}>
            {processingMedia ? (
              <div className={styles.processingOverlay} role="status" aria-live="polite">
                <div className={styles.processingPanel}>
                  <div className={styles.processingSpinner} aria-hidden="true" />
                  <span className={styles.processingEyebrow}>Processing media</span>
                  <h2>Building your drop preview</h2>
                  <p>Please keep this page open while the video crop and soundtrack finish uploading and rendering.</p>
                </div>
              </div>
            ) : null}

            <div className={styles.formIntro}>
              <h3>{showFields ? 'Drop Fields' : 'Drop Media'}</h3>
              <p>{showFields ? 'The start time uses the selected timezone, and the end time is derived automatically from the chosen duration.' : 'Start with a video. After that, add the soundtrack and process the collectible preview.'}</p>
            </div>

            <FormField label="Video" helper="Upload an MP4, MOV, or M4V up to about 10 seconds.">
              {(phase === 'idle' || (!showFields && !mediaEditor.previewUrl && !draft.media?.[0]?.source?.storagePath)) ? (
                <label className={styles.secondaryButton}>
                  Choose Video
                  <input type="file" accept=".mp4,.mov,.m4v,video/*" onChange={handleVideoChange} hidden />
                </label>
              ) : null}

              {(phase === 'video_selected' || phase === 'media_ready_to_process') && mediaEditor.previewUrl ? (
                <div className={styles.mediaComposer}>
                  <div className={styles.cropperShell}>
                    <Cropper
                      video={mediaEditor.previewUrl}
                      crop={mediaEditor.crop}
                      zoom={mediaEditor.zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={(crop) => setMediaEditor((prev) => ({ ...prev, crop }))}
                      onZoomChange={(zoom) => setMediaEditor((prev) => ({ ...prev, zoom }))}
                      onCropComplete={(_, croppedAreaPixels) => setMediaEditor((prev) => ({ ...prev, croppedAreaPixels }))}
                    />
                    <div className={styles.cropGuide} aria-hidden="true">
                      <div className={styles.cropGuideInner} />
                    </div>
                  </div>
                  <div className={styles.mediaComposerControls}>
                    <label className={styles.sliderLabel} htmlFor="drop-video-zoom">
                      <span>Zoom</span>
                    </label>
                    <input
                      id="drop-video-zoom"
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={mediaEditor.zoom}
                      onChange={(event) => setMediaEditor((prev) => ({ ...prev, zoom: Number(event.target.value) }))}
                    />
                    <div className={styles.mediaStatusRow}>
                      <span className={styles.statusPill}>{mediaStatus}</span>
                      <span>{formatSeconds(mediaEditor.durationSeconds)}</span>
                    </div>
                    <div className={styles.mediaPickerStack}>
                      <label className={styles.secondaryButton}>
                        Change Video
                        <input type="file" accept=".mp4,.mov,.m4v,video/*" onChange={handleVideoChange} hidden />
                      </label>
                      <div className={styles.mediaSummary}>
                        <strong>Soundtrack Rules</strong>
                        <span>Max length: {AUDIO_MAX_DURATION_SECONDS} seconds</span>
                        <span>Allowed files: {AUDIO_FILE_TYPES_LABEL}</span>
                      </div>
                      <label className={styles.secondaryButton}>
                        {audioEditor.file || draft.soundtrack?.source?.storagePath ? 'Change Music' : 'Choose Music'}
                        <input type="file" accept={AUDIO_ACCEPT} onChange={handleAudioChange} hidden />
                      </label>
                    </div>
                    <div className={styles.mediaSummary}>
                      <strong>Soundtrack</strong>
                      <span>{audioEditor.file?.name || draft.soundtrack?.source?.storagePath?.split('/').pop() || 'No music selected yet'}</span>
                      <span>
                        {audioEditor.file || draft.soundtrack?.source?.storagePath
                          ? `${formatSeconds(audioEditor.durationSeconds || draft.soundtrack?.source?.durationSeconds || draft.soundtrack?.durationSeconds)} • ${AUDIO_MAX_DURATION_SECONDS} sec max`
                          : `Upload ${AUDIO_FILE_TYPES_LABEL}`}
                      </span>
                    </div>
                    <button type="button" className={styles.primaryButton} onClick={handleProcessMedia} disabled={!allowProcessing || !artistAccess.isActive}>
                      Process Media
                    </button>
                  </div>
                </div>
              ) : null}

              {(phase === 'video_selected' || phase === 'media_ready_to_process') && !mediaEditor.previewUrl && draft.media?.[0]?.source?.storagePath ? (
                <div className={styles.noticeBox}>
                  <strong>Pick the video again to edit the crop.</strong>
                  <p>The draft has a stored source file, but the cropper needs a local video selection to show the exact circular frame.</p>
                  <label className={styles.secondaryButton}>
                    Choose Video
                    <input type="file" accept=".mp4,.mov,.m4v,video/*" onChange={handleVideoChange} hidden />
                  </label>
                </div>
              ) : null}

              {phase === 'preview_ready' && renderedPreviewPath ? (
                <div className={styles.previewStage}>
                  <CollectiblePreviewSurface
                    hasPlayableVideo={hasPlayableCollectibleVideo}
                    mediaUrl={renderedPreviewUrl}
                    playbackUrl={renderedPlaybackUrl}
                    posterUrl={renderedPosterUrl || renderedPreviewUrl}
                    backTextureUrl={artistSignatureUrl}
                    title={draft.title || 'Drop preview'}
                    placeholderLabel="Rendered Preview"
                    paneClassName={styles.renderedPreviewPane}
                    stageClassName={styles.renderedPreviewStage}
                    viewerClassName={styles.renderedPreviewViewer}
                    placeholderClassName={styles.previewPlaceholder}
                  />
                  <div className={styles.previewControls}>
                    <div className={styles.mediaSummaryCard}>
                      <strong>Processed collectible</strong>
                      <span>Video {formatSeconds(draft.media?.[0]?.source?.durationSeconds)}</span>
                      <span>Music {formatSeconds(draft.soundtrack?.durationSeconds || draft.soundtrack?.source?.durationSeconds)}</span>
                    </div>
                    <div className={styles.mediaPickerStack}>
                      <label className={styles.secondaryButton}>
                        Change Video
                        <input type="file" accept=".mp4,.mov,.m4v,video/*" onChange={handleVideoChange} hidden />
                      </label>
                      <div className={styles.mediaSummary}>
                        <strong>Soundtrack Rules</strong>
                        <span>Max length: {AUDIO_MAX_DURATION_SECONDS} seconds</span>
                        <span>Allowed files: {AUDIO_FILE_TYPES_LABEL}</span>
                      </div>
                      <label className={styles.secondaryButton}>
                        Change Music
                        <input type="file" accept={AUDIO_ACCEPT} onChange={handleAudioChange} hidden />
                      </label>
                    </div>
                  </div>
                </div>
              ) : null}
            </FormField>

            {showFields ? (
              <>
                <FormField label="Title">
                  <input value={draft.title} onChange={(event) => updateField('title', event.target.value)} required />
                </FormField>

                <FormField label="Description">
                  <textarea value={draft.description} onChange={(event) => updateField('description', event.target.value)} rows={5} required />
                </FormField>

                <div className={styles.grid}>
                  <FormField label="Price">
                    <input value={draft.priceAmount} onChange={(event) => updateField('priceAmount', event.target.value)} type="number" min="0.01" step="0.01" required />
                  </FormField>
                  <FormField label="Currency">
                    <select value={draft.currency} onChange={(event) => updateField('currency', event.target.value)}>
                      {SUPPORTED_CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Supply">
                    <input value={draft.maxSupply} onChange={(event) => updateField('maxSupply', event.target.value)} type="number" min="1" step="1" required />
                  </FormField>
                  <FormField label="Start Date and Time">
                    <input value={draft.saleStartAt} onChange={(event) => updateField('saleStartAt', event.target.value)} type="datetime-local" required />
                  </FormField>
                  <FormField label="Timezone">
                    <select value={draft.timezone} onChange={(event) => updateField('timezone', event.target.value)}>
                      {timezoneOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Duration Hours">
                    <select value={draft.durationHours} onChange={(event) => updateField('durationHours', Number(event.target.value))}>
                      {Array.from({ length: 12 }, (_, index) => index + 1).map((hours) => (
                        <option key={hours} value={hours}>{hours} hour{hours === 1 ? '' : 's'}</option>
                      ))}
                    </select>
                  </FormField>
                </div>

                <div className={styles.readOnlyBox}>
                  <strong>Derived live window</strong>
                  <span className={styles.readOnlySubtext}>
                    {formatDraftDateTime(draft.saleStartAt, draft.timezone)} to {derivedEnd ? formatDraftDateTime(derivedEnd, draft.timezone) : 'TBD'}
                  </span>
                </div>

                <div className={styles.actions}>
                  <button type="button" className={styles.secondaryButton} onClick={handleSave} disabled={saving || processingMedia || !artistAccess.isActive}>
                    {saving ? 'Saving…' : 'Save Draft'}
                  </button>
                  <button type="button" className={styles.primaryButton} onClick={handlePublish} disabled={publishing || processingMedia || !artistAccess.isActive || !mediaReady}>
                    {publishing ? 'Publishing…' : 'Publish Drop'}
                  </button>
                </div>
              </>
            ) : null}
          </form>
        ) : null}
      </div>
    </div>
  );
}
